import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Drawer,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'

import DateRangePicker from '../../../../../../../components/DateRangePicker'
import {
  EOrderDateType,
  EOrderStatus,
  EOrderType,
} from '../../../../../../../types/order'
import useAxios from '../../../../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../../../../types/pagination'
import { IProduct } from '../../../../../../../types/product'
import { ICategory } from '../../../../../../../types/category'
import { IDistributionChannel } from '../../../../../../../types/distribution-channel'
import SelectSearchMultiple, {
  IOptionSelect,
} from '../../../../../../../components/SelectSearchMultiple'
import axios from 'axios'
import { validateMinMaxValue } from '../../../../../../../utils/validateMinMaxValue'
import { IFilterMapped } from './ShopeeOrderFilter'
import SelectSearch from '../../../../../../../components/SelectSearch'
import { IUser, IUserLists } from '../../../../../../../types/user'
import ProductSelectSearch from '../../../../../../../components/SelectSearch/ProductSelectSearch'

export enum EOrderOverdue {
  TRUE = 'true',
  FALSE = 'false',
  NULL = 'null',
}

export enum EStatusTypeOrderCrmTelesale {
  ORDER = 'order',
  CRM = 'crm',
  TELESALE = 'telesale',
}

export enum EStatusOrderShowTracking {
  TRACKING = 'tracking',
  NO_TRACKING = 'no_tracking',
  PENDING = 'pending',
  BANK = 'BANK',
  COD = 'COD',
}

interface OrderFilterDrawerProps {
  isOpen: boolean
  onClose: () => void
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onSearch: (filter: IFilterMapped) => void
}

const menuItemsTypeDate = [
  {
    label: 'ทั้งหมด',
    value: undefined,
  },
  {
    label: 'วันที่สร้าง',
    value: EOrderDateType.CREATED,
  },
  {
    label: 'วันที่ย้อนหลัง',
    value: EOrderDateType.ORDERDATE,
  },
  {
    label: 'วันที่รับสินค้า',
    value: EOrderDateType.PICKUP,
  },
  {
    label: 'วันที่พิมพ์',
    value: EOrderDateType.PRINTED,
  },
]

const menuItemsTypeOrderStatusCRM = [
  {
    label: 'ทั้งหมด',
    value: undefined,
  },
  {
    label: 'รายการคำสั่งซื้อ',
    value: EStatusTypeOrderCrmTelesale.ORDER,
  },
  {
    label: 'รายการคำสั่งซื้อ CRM',
    value: EStatusTypeOrderCrmTelesale.CRM,
  },
  {
    label: 'รายการคำสั่งซื้อ Telesale',
    value: EStatusTypeOrderCrmTelesale.TELESALE,
  },
]

const menuItemsTypeStatus = [
  {
    label: 'ทั้งหมด',
    value: undefined,
  },
  {
    label: 'เฉพาะรายการที่มี Tracking',
    value: EStatusOrderShowTracking.TRACKING,
  },
  {
    label: 'ยังไม่มี Tracking',
    value: EStatusOrderShowTracking.NO_TRACKING,
  },
  {
    label: 'สินค้าค้างส่ง',
    value: EStatusOrderShowTracking.PENDING,
  },
  {
    label: 'เฉพาะโอนเงิน',
    value: EStatusOrderShowTracking.BANK,
  },
  {
    label: 'เฉพาะ COD',
    value: EStatusOrderShowTracking.COD,
  },
]

const menuItemsTypePaymentMethod = [
  {
    label: 'ทั้งหมด',
    value: undefined,
  },
  {
    label: 'เก็บเงินปลายทาง',
    value: EOrderType.BANK,
  },
  {
    label: 'โอนเงินผ่านธนาคาร',
    value: EOrderType.COD,
  },
]

interface ICity {
  id: number
  name_th: string
  name_en: string
  geography_id: number
  created_at: string
  updated_at: string
  deleted_at: string | null
}

const OrderFilterDrawer = ({
  isOpen,
  onClose,
  onFilterChange,
  filter,
  onSearch,
}: OrderFilterDrawerProps) => {
  const {
    page,
    perPage,
    search,
    startDate,
    endDate,
    categories,
    productPrice,
    products,
    orderType,
    orderStatus,
    isPrinted,
  } = filter
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [searchProduct, setSearchProduct] = useState<string>('')
  const [searchCategory, setSearchCategory] = useState<string>('')

  const handleFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      onFilterChange({ ...filter, ...newFilter })
    },
    [filter, onFilterChange],
  )

  const handleReset = useCallback(() => {
    onFilterChange({
      page: undefined,
      perPage: undefined,
      search: undefined,
      startDate: undefined,
      endDate: undefined,
      categories: undefined,
      productPrice: undefined,
      products: undefined,
      orderType: undefined,
      orderStatus: undefined,
      isPrinted: undefined,
    })
    onSearch({
      page: page,
      perPage: perPage,
      startDate: undefined,
      endDate: undefined,
      categories: undefined,
      productPrice: undefined,
      products: undefined,
      orderType: undefined,
      orderStatus: undefined,
      isPrinted: undefined,
    })
  }, [page, perPage, onFilterChange, onSearch])

  const productsList = useAxios<PaginationResponseInterface<IProduct>>({
    method: 'GET',
    url: '/product',
    params: {
      page: 1,
      perPage: 5,
      search: searchProduct,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchProduct = async (value: string) => {
    setSearchProduct(value)
    await productsList.mutate()
  }

  const categoriesList = useAxios<PaginationResponseInterface<ICategory>>({
    method: 'GET',
    url: '/category',
    params: {
      page: 1,
      perPage: 5,
      search: searchCategory,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchCategory = async (value: string) => {
    setSearchCategory(value)
    await categoriesList.mutate()
  }

  return (
    <Drawer
      anchor={'right'}
      keepMounted
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        style: {
          width: isMobile ? '80%' : '40%',
          maxWidth: isMobile ? '80%' : '40%',
        },
      }}
    >
      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item xs={12} lg={12}>
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            label={'ค้นหาจากรหัสคำสั่งซื้อ, ชื่อลูกค้า, หมายเลขโทรศัพท์'}
            fullWidth
            id="search"
            name="search"
            autoFocus
            variant="filled"
            value={search || ''}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </Grid>
        <Grid item xs={6} lg={6}>
          <TextField
            fullWidth
            id="minTotal"
            name="minTotal"
            label={'ช่วงราคา (น้อยที่สุด)'}
            variant={'filled'}
            type={'number'}
            value={productPrice || ''}
            onChange={(e) =>
              handleFilterChange({
                productPrice:
                  validateMinMaxValue(e.target.value, 0, 999999) || undefined,
              })
            }
          />
        </Grid>
        <Grid item xs={12} lg={12}>
          <SelectSearchMultiple<ICategory>
            placeholder={'เลือกหมวดหมู่สินค้า'}
            label={'เลือกหมวดหมู่สินค้า'}
            onSelect={(e) => {
              const selectedCategoryIds = e?.map(
                (category) => category?.value?.name,
              )
              onFilterChange({ ...filter, categories: selectedCategoryIds })
            }}
            search={searchCategory}
            onSearch={handleSearchCategory}
            options={
              categoriesList.data?.records
                .filter(
                  (category: ICategory) => !categories?.includes(category.name),
                )
                .map(
                  (category: ICategory) =>
                    category.isAvailable && {
                      label: category.name,
                      value: category,
                    },
                )
                .reduce((acc: IOptionSelect<ICategory>[], category) => {
                  if (category) {
                    acc.push(category)
                  }
                  return acc
                }, []) || ([] as IOptionSelect<ICategory>[])
            }
          />
        </Grid>
        <Grid item xs={12} lg={12}>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={(startDate, endDate) => {
              handleFilterChange({ startDate, endDate })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={12}>
          <ProductSelectSearch
            products={products}
            handleFilterChange={handleFilterChange}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="orderType-label">ประเภทการชำระเงิน</InputLabel>
            <Select
              id="orderType"
              name="orderType"
              labelId="orderType-label"
              value={orderType}
              onChange={(e) =>
                handleFilterChange({ orderType: e?.target.value as EOrderType })
              }
              fullWidth
            >
              <MenuItem disabled value="" selected>
                <em>เลือกประเภทการชำระเงิน</em>
              </MenuItem>
              {menuItemsTypePaymentMethod.map((item, index: number) => (
                <MenuItem value={item.value} key={index}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} lg={6}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="orderStatus-label">สถานะคำสั่งซื้อ</InputLabel>
            <Select
              id="orderStatus"
              name="orderStatus"
              labelId="orderStatus-label"
              value={orderStatus}
              onChange={(e) =>
                handleFilterChange({
                  orderStatus: e?.target.value as EOrderStatus,
                })
              }
              fullWidth
            >
              <MenuItem disabled value="" selected>
                <em>เลือกสถานะคำสั่งซื้อ</em>
              </MenuItem>
              {menuItemsTypeOrderStatusCRM.map((item, index: number) => (
                <MenuItem value={item.value} key={index}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} lg={6}>
          <FormControl variant="filled" fullWidth>
            <InputLabel id="isPrinted-label">พิมพ์</InputLabel>
            <Select
              id="isPrinted"
              name="isPrinted"
              labelId="isPrinted-label"
              value={isPrinted}
              onChange={(e) =>
                handleFilterChange({ isPrinted: e?.target.value as boolean })
              }
              fullWidth
            >
              <MenuItem disabled value="" selected>
                <em>เลือกการพิมพ์</em>
              </MenuItem>
              <MenuItem value={'true'}>พิมพ์แล้ว</MenuItem>
              <MenuItem value={'false'}>ยังไม่พิมพ์</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={9} lg={9}>
          <Button
            color="primary"
            size="large"
            fullWidth
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => onSearch(filter)}
            sx={{ height: '100%' }}
          >
            ค้นหา
          </Button>
        </Grid>
        <Grid item xs={3} lg={3}>
          <Button
            color="inherit"
            size="large"
            fullWidth
            variant="contained"
            startIcon={!isMobile && <ClearIcon />}
            onClick={() => handleReset()}
            sx={{ height: '100%' }}
          >
            {isMobile ? <ClearIcon /> : 'รีเซ็ต'}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  )
}

export default OrderFilterDrawer
